<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";

import theCard from "@/components/theCard.vue";
import theProductCard from "@/components/theProductCard.vue";
import { useCardBookmark } from "@/composables/useCardBookmark";
import { useCardTelemetry } from "@/composables/useCardTelemetry";
import isUnauth from "@/utils/isUnauth";

interface Props {
  result: any;
  //eslint-disable-next-line vue/no-unused-properties
  index?: number;
  status?: any;
  forCarousel?: boolean;
  cardView?: string;
  cardStyle?: string;
  priceView?: string;
  topicView?: boolean;
  isStoreContent?: boolean;
  showDescription?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  index: 0,
  status: null,
  forCarousel: false,
  cardView: "Grid",
  cardStyle: "Grid",
  priceView: "USD",
  topicView: false,
  isStoreContent: false,
  showDescription: true,
});

const router = useRouter();

// Composables
const {
  bookmarkMe,
  debouncedBookmarkMe,
  addContentToMyList,
  removeContentFromMyList,
} = useCardBookmark();

const { sendTelemetry: sendTelemetryBase } = useCardTelemetry();

// Computed properties
const resultLink = computed(() => {
  const gradedLabId = props.result?.friendly_url || props.result?.id;

  return gradedLabId
    ? router.resolve({ name: "lab", params: { labId: gradedLabId } }).href
    : "";
});

// Methods
const sendTelemetry = async () => {
  await sendTelemetryBase(props, resultLink.value);
};

// Expose methods for testing
defineExpose({
  addContentToMyList,
  removeContentFromMyList,
  bookmarkMe,
  debouncedBookmarkMe,
  sendTelemetry,
  resultLink,
});
</script>

<template>
  <div
    :class="[
      'relative z-10 h-full hover:z-[700] focus:z-[700] active:z-[700]',
      {
        'w-[19rem]': props.cardView === 'Grid' && !topicView,
        'w-full': props.cardView !== 'Grid' && !topicView,
        'w-[22rem]': topicView,
      },
    ]"
    data-cy="graded-lab-result"
    v-show="result"
  >
    <the-card
      v-if="!isStoreContent"
      :is-unauth="isUnauth()"
      :for-carousel="forCarousel"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :result="result"
      :status="status"
      :cy="'graded-lab-card'"
      :link="resultLink"
      :purchaseType="'purchase'"
      :show-description="props.showDescription"
      @navigate="sendTelemetry"
      @bookmark="debouncedBookmarkMe(result)"
    />
    <the-product-card
      v-else
      :is-unauth="isUnauth()"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :price-view="priceView"
      :result="result"
      :status="status"
      :cy="'graded-lab-product-card'"
      :link="resultLink"
      :show-description="props.showDescription"
      @navigate="sendTelemetry"
      @bookmark="debouncedBookmarkMe(result)"
    />
  </div>
</template>
